import { useMutation } from '@apollo/client';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useToast } from '@quality24/design-system';
import { ANSWER_MUTATION, REFUSE_MUTATION } from './graphql';
import { ComposedReleasedSurvey, HookSurveyAnswer, SurveyHook } from './types';
import { checkCanNext, initAnswers } from './utils';

const getValidationSchema = (t: TFunction) =>
  Yup.array().of(
    Yup.object().shape({
      id: Yup.string().required(t('validation.required')),
      releasedSurveyId: Yup.string().required(t('validation.required')),
      questionId: Yup.string().required(t('validation.required')),
      value: Yup.object().shape({
        score: Yup.number(),
        smileyScore: Yup.number(),
        improvements: Yup.array().of(Yup.string()),
        text: Yup.string(),
      }),
    }),
  );

export const useSurvey = (
  releasedSurvey: ComposedReleasedSurvey,
  onSubmit?: () => void,
  onRefuse?: () => void,
): SurveyHook => {
  const [idx, setIdx] = React.useState(0);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentPageIndex = searchParams.get('page');

  React.useEffect(() => {
    const newIndex = Number(currentPageIndex);
    if (Number.isInteger(newIndex)) {
      setIdx(newIndex);
    }
  }, [currentPageIndex]);

  const { toast, error: errorToast } = useToast();
  const { t } = useTranslation();
  const validationSchema = React.useMemo(() => getValidationSchema(t), [t]);

  const handleMutationError = React.useCallback(
    () => errorToast(t('errors.surveyRequestFailed')),
    [errorToast, t],
  );

  const [answerSurvey, { data, error, loading: answerLoading }] = useMutation(
    ANSWER_MUTATION,
    {
      onCompleted: () => {
        toast(t('survey.success'));
        if (onSubmit) onSubmit();
      },
      onError: handleMutationError,
    },
  );

  const [refuseSurvey, { loading: refuseLoading }] = useMutation(
    REFUSE_MUTATION,
    {
      variables: { id: releasedSurvey.id },
      onCompleted: () => {
        if (onRefuse) onRefuse();
      },
      onError: handleMutationError,
    },
  );

  const formik = useFormik<Array<HookSurveyAnswer>>({
    initialValues: initAnswers(releasedSurvey),
    validationSchema,
    onSubmit: async (values) => {
      // Filter out empty answers
      const answers = values
        .filter((v) => {
          if (v.category === 'STATIC_PAGE') return false;
          return (
            v.value.score ||
            v.value.smileyScore ||
            v.value.improvements?.length ||
            v.value.text
          );
        })
        .map((answer) => ({
          releasedSurveyId: answer.releasedSurveyId,
          questionId: answer.questionId,
          value: {
            ...answer.value,
            text: answer.value.text ? answer.value.text : undefined,
          },
          data: answer.data,
        }));

      await answerSurvey({
        variables: {
          input: {
            releasedSurvey: { id: releasedSurvey.id },
            answers,
          },
        },
      });
    },
  });

  const { pages } = releasedSurvey;

  // Survey navigation
  const maxPages = React.useMemo(() => pages.length, [pages.length]);
  const hasNext = idx < maxPages - 1;
  const hasPrevious = idx > 0;

  /**
   * Move state to next page
   */
  const next = React.useCallback(() => {
    if (!hasNext) return;
    navigate(`?page=${idx + 1}`);
    setIdx((prev) => prev + 1);
  }, [hasNext, idx, navigate]);

  /**
   * Move state to previous page
   */
  const previous = React.useCallback(() => {
    if (!hasPrevious) return;
    navigate(`?page=${idx - 1}`);
    setIdx((prev) => prev - 1);
  }, [hasPrevious, idx, navigate]);

  return {
    pages,
    currentPage: pages[idx],
    nav: {
      hasNext,
      hasPrevious,
      canSkip: !pages[idx].required && !pages[idx].data.required,
      canNext: checkCanNext(pages[idx], formik.values[idx]),
      next,
      previous,
      submit: formik.handleSubmit,
      refuse: refuseSurvey,
      answersAmount: formik.values.filter((v) => {
        if (v.category === 'STATIC_PAGE') return false;
        return (
          v.value.score ||
          v.value.smileyScore ||
          v.value.improvements?.length ||
          v.value.text
        );
      }).length,
      currentPageIndex: idx,
      totalPages: maxPages - 1,
    },
    answer: {
      values: formik.values[idx] || {},
      errors: formik.errors[idx] || {},
      setFieldValue: (field, value, shouldValidate) => {
        formik.setFieldValue(`[${idx}].${field}`, value, shouldValidate);
      },
    },
    isValid: formik.isValid,
    operation: { data, error, loading: answerLoading || refuseLoading },
  };
};
